import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';

const USERNAME_DOMAIN = 'izzy.local';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  isSuperUsuario: boolean;
  isActive: boolean;
  signIn: (username: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  reloadProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // Mantener el callback sincrónico: solo actualizamos el estado local.
      setSession(nextSession);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user?.id ?? null;

  const fetchProfile = useCallback(async (): Promise<Profile | null> => {
    if (!userId) return null;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      return error ? null : (data as Profile);
    } catch {
      return null;
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      return undefined;
    }

    let active = true;
    setProfileLoading(true);

    (async () => {
      const nextProfile = await fetchProfile();
      if (!active) return;
      setProfile(nextProfile);
      if (active) setProfileLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [userId, fetchProfile]);

  const signIn = useCallback(
    async (username: string, password: string): Promise<{ error: string | null }> => {
      const clean = username.trim().toLowerCase();
      if (!clean || !password) {
        return { error: 'Ingrese usuario y contraseña.' };
      }
      try {
        const { data: signInData, error } = await supabase.auth.signInWithPassword({
          email: `${clean}@${USERNAME_DOMAIN}`,
          password,
        });
        if (error || !signInData.user) {
          return { error: 'Usuario o contraseña incorrectos.' };
        }

        // Verificamos el estado del usuario antes de permitir el acceso.
        const { data: accountProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', signInData.user.id)
          .maybeSingle();

        if (accountProfile && (accountProfile as Profile).status === 'INACTIVO') {
          await supabase.auth.signOut();
          return { error: 'Su usuario está inactivo. Contacte al administrador del sistema.' };
        }

        return { error: null };
      } catch {
        return { error: 'No se pudo conectar con el servidor. Intente nuevamente.' };
      }
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
    } finally {
      setProfile(null);
      setSession(null);
    }
  }, []);

  const reloadProfile = useCallback(async () => {
    if (!userId) return;
    const nextProfile = await fetchProfile();
    setProfile(nextProfile);
  }, [userId, fetchProfile]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      profileLoading,
      isSuperUsuario: profile?.role === 'superUsuario',
      isActive: profile?.status !== 'INACTIVO',
      signIn,
      signOut,
      reloadProfile,
    }),
    [session, profile, loading, profileLoading, signIn, signOut, reloadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}